
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppFeaturesModal from './AppFeaturesModal'
import BenefitsModal from './BenefitsModal'
import RegistrationModal from './RegistrationModal'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import PromoBannerModal from './PromoBannerModal'

const InfoGridModal = ({ open }) => {
  const { t } = useTranslation()
  const [viewport, setViewport] = useState(window.innerWidth)
  const isMobile = viewport <= 768 ||
  (viewport <= 950 && window.innerHeight <= 500 && window.innerWidth > window.innerHeight)

const supportsHover = window.matchMedia(
  '(hover: hover) and (pointer: fine)'
).matches

  const [anchorRect, setAnchorRect] = useState(null)
  const [anchorRect2, setAnchorRect2] = useState(null)
  const [anchorRect3, setAnchorRect3] = useState(null)

  const [showHint, setShowHint] = useState(false)
  const [showHint2, setShowHint2] = useState(false)
  const [showHint3, setShowHint3] = useState(false)

  const [tooltipBlocked, setTooltipBlocked] = useState({
    1: false,
    2: false,
    3: false
  })

  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

  const [hoverBox4, setHoverBox4] = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [membershipsCount, setMembershipsCount] = useState(0)
  const [foundingMembershipLimit, setFoundingMembershipLimit] = useState(0)

  useEffect(() => {
    const loadMembershipsCount = async () => {
      const res = await fetchWithAuth('/public/stats', {
        cacheTtlMs: 60000
      })

      if (!res.ok) return

      const data = await res.json()

      setMembershipsCount(Number(data?.membershipsCount || 0))
      setFoundingMembershipLimit(
        Number(data?.foundingMembershipLimit || 0)
      )
    }

    loadMembershipsCount()
  }, [])

  const foundingRemaining = Math.max(
    foundingMembershipLimit - membershipsCount,
    0
  )

  const foundingRemainingFormatted = new Intl.NumberFormat('sk-SK').format(
    foundingRemaining
  )

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth)

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!showHint && !showHint2 && !showHint3) return

    const timer = setTimeout(() => {
      setShowHint(false)
      setShowHint2(false)
      setShowHint3(false)

      setTooltipBlocked(prev => ({
        ...prev,
        ...(showHint && { 1: true }),
        ...(showHint2 && { 2: true }),
        ...(showHint3 && { 3: true })
      }))
    }, 3000)

    return () => clearTimeout(timer)
  }, [showHint, showHint2, showHint3])

  useEffect(() => {
    const handleClickOutside = (e) => {
      const modalOpen = anchorRect || anchorRect2 || anchorRect3

      if (!modalOpen) return

      setAnchorRect(null)
      setAnchorRect2(null)
      setAnchorRect3(null)

      e.stopPropagation()
      e.preventDefault()
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [anchorRect, anchorRect2, anchorRect3])

  if (!open) return null

  const showFeatureModal = !!anchorRect
  const showRegModal = !!anchorRect3

  let gridStyle = {
    position: 'fixed',
    display: 'grid',
    zIndex: 800
  }

  let boxWidth = '16vw'
  let boxHeight = '10vw'
  let gap = 28

  if (viewport >= 2560) {
    gridStyle = {
      ...gridStyle,
      top: '53%',
      left: '33%',
      transform: 'translate(-50%, -50%)'
    }
    boxWidth = '26vw'
    boxHeight = '12vw'
    gap = 40
  } else if (viewport >= 1920) {
    gridStyle = {
      ...gridStyle,
      top: '53%',
      left: '30%',
      transform: 'translate(-50%, -50%)'
    }
    boxWidth = '26vw'
    boxHeight = '13vw'
    gap = 32
  } else if (viewport >= 1366) {
    gridStyle = {
      ...gridStyle,
      top: '54.4%',
      left: '31%',
      transform: 'translate(-50%, -50%)'
    }
    boxWidth = '26vw'
    boxHeight = '14vw'
    gap = 24
  } else {
    gridStyle = {
      ...gridStyle,
      top: '62%',
      left: '20%',
      transform: 'translate(-30%, -65%)'
    }
    boxWidth = '26vw'
    boxHeight = '16vw'
    gap = 18
  }

  // Mobile: original 26:16 aspect ratio.
  // The whole grid is one flex item, preventing horizontal overflow.
  const mobileGridWidth = Math.max(0, viewport - 36)
  const mobileGap = 8
  const mobileBoxWidth = (mobileGridWidth - mobileGap) / 2
  const mobileBoxHeight = mobileBoxWidth * 16 / 26

  // All mobile typography and spacing scale with box width.
  const mobileScale = mobileBoxWidth / 355

  const mobileFont = (desktopPx) =>
    `${desktopPx * mobileScale}px`

  return (
    <>
      <div
        style={isMobile
          ? {
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              flex: '0 0 100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }
          : {
              display: 'contents'
            }}
      >
        <div
          style={{
            position: isMobile ? 'relative' : 'fixed',
            top: isMobile ? 'auto' : '105px',
            left: isMobile ? 'auto' : '50%',
            transform: isMobile ? 'none' : 'translateX(-50%)',
            zIndex: 801,
            color: '#0A303B',
            fontSize: isMobile ? 14 : '1.2vw',
            fontWeight: 700,
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            textAlign: 'center',
            pointerEvents: 'none',
            width: isMobile ? '100%' : undefined,
            marginBottom: isMobile ? 12 : 0
          }}
        >
          {t('SLOGAN2')}
        </div>

        <div
          style={{
            ...gridStyle,
            ...(isMobile
              ? {
                  position: 'relative',
                  top: 'auto',
                  left: 'auto',
                  transform: 'none',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  margin: 0
                }
              : {}),
            gridTemplateColumns: isMobile
              ? 'repeat(2, minmax(0, 1fr))'
              : `repeat(2, ${boxWidth})`,
            gridTemplateRows: isMobile
              ? `repeat(2, ${mobileBoxHeight}px)`
              : `repeat(2, ${boxHeight})`,
            gap: isMobile ? mobileGap : gap
          }}
        >
          {[4, 1, 2, 3].map((num, i) => {
            const boxData = t(`infoGrid.box${num}`, {
              returnObjects: true,
              count: foundingMembershipLimit
            })

            const lineKeys = Object.keys(boxData)
              .filter(key => key.startsWith('line'))
              .sort()

            const isFirstBox = num === 1
            const isSecondBox = num === 2
            const isThirdBox = num === 3
            const isFourthBox = num === 4
            const isHoverable = num !== 4

            return (
              <div
                key={i}
		onMouseEnter={(e) => {
		  if (!supportsHover) return

 		 if (isFirstBox && !tooltipBlocked[1]) setShowHint(true)
 		 if (isSecondBox && !tooltipBlocked[2]) setShowHint2(true)
  		if (isThirdBox && !tooltipBlocked[3]) setShowHint3(true)

 		 if (isFourthBox) setHoverBox4(true)

  		if (isHoverable) {
  		  e.currentTarget.style.background =
  		    'rgba(10,48,59,0.15)'
		  }
		}}
                onMouseMove={(e) => {
                  if (isFirstBox || isSecondBox || isThirdBox) {
                    setCursorPos({
                      x: e.clientX,
                      y: e.clientY
                    })
                  }
                }}
		onMouseLeave={(e) => {
		  if (!supportsHover) return

		  if (isFirstBox && !tooltipBlocked[1]) setShowHint(false)
 		 if (isSecondBox && !tooltipBlocked[2]) setShowHint2(false)
 		 if (isThirdBox && !tooltipBlocked[3]) setShowHint3(false)

 		 if (isFourthBox) setHoverBox4(false)

 		 if (isHoverable) {
 		   e.currentTarget.style.background =
 		     'rgba(16,124,107,0.06)'
 		 }
		}}
		onClick={(e) => {
		  if (isFourthBox) {
		    if (isMobile && !hoverBox4) {
 		     setHoverBox4(true)
 		     return
 		   }

 		   setShowRegisterModal(true)
  		  return
  		}

                  const r = e.currentTarget.getBoundingClientRect()

                  if (isFirstBox) {
                    setAnchorRect(r)
                    setAnchorRect2(null)
                    setAnchorRect3(null)
                  }

                  if (isSecondBox) {
                    return
                  }

                  if (isThirdBox) {
                    setAnchorRect3(r)
                    setAnchorRect(null)
                    setAnchorRect2(null)
                  }
                }}
                style={{
                  position: 'relative',
                  background: num === 4
                    ? 'transparent'
                    : 'rgba(16,124,107,0.06)',
                  border: '1.5px solid #0A303B',
                  borderRadius: 10,
                  padding: isMobile ? mobileFont(10) : 10,
                  width: '100%',
                  height: '100%',
                  minWidth: 0,
                  minHeight: 0,
                  boxSizing: 'border-box',
                  color: '#0A303B',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: num === 4 ? 'center' : 'flex-start',
                  textAlign: num === 4 ? 'center' : 'left',
                  cursor: 'pointer',
                  boxShadow: num === 4
                    ? 'none'
                    : '0 12px 28px rgba(0,0,0,0.25)',
                  transition: isHoverable
                    ? 'background 0.25s ease'
                    : 'none'
                }}
              >
                {isFourthBox && hoverBox4 && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(30,63,68,0.9)',
                      borderRadius: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      zIndex: 10,
                      padding: isMobile ? mobileFont(14) : '1vw'
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile
                          ? mobileFont(24)
                          : '1.8vw',
                        fontWeight: 800,
                        color: '#E7DFEC'
                      }}
                    >
                      {t('REGISTER_NOW')}
                    </div>

                    <div
                      style={{
                        marginTop: isMobile
                          ? mobileFont(3)
                          : '0.2vw',
                        fontSize: isMobile
                          ? mobileFont(10)
                          : '0.7vw',
                        fontWeight: 500,
                        color: '#E7DFEC',
                        textAlign: 'center'
                      }}
                    >
                      {t('REGISTER_ON_MOBILE')}
                    </div>

                    <div
                      style={{
                        marginTop: isMobile
                          ? mobileFont(4)
                          : '0.3vw',
                        fontSize: isMobile
                          ? mobileFont(11)
                          : '0.8vw',
                        fontWeight: 500,
                        color: 'rgba(59,198,196,0.82)',
                        lineHeight: 1.4
                      }}
                    >
                      {t('FREE_TRIAL_INFO')}
                    </div>
                  </div>
                )}

                <h3
                  style={{
                    margin: 0,
                    marginBottom: isMobile
                      ? mobileFont(num === 4 ? 1 : 4)
                      : num === 4
                        ? '0.1vw'
                        : '0.3vw',
                    fontSize: isMobile
                      ? mobileFont(14)
                      : '1vw',
                    alignSelf: 'center',
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  {boxData.title}
                </h3>

                {isSecondBox ? (
                  <PromoBannerModal
                    fallback={lineKeys.map((lineKey, index) => {
                      const text = boxData[lineKey]

                      return (
                        <p
                          key={index}
                          style={{
                            margin: 0,
                            fontSize: isMobile
                              ? mobileFont(11)
                              : '0.8vw',
                            lineHeight: '1.45'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: text.replace(/\n/g, '<br />')
                          }}
                        />
                      )
                    })}
                  />
                ) : (
                  lineKeys.map((lineKey, index) => {
                    const text = boxData[lineKey]

                    if (num === 4 && lineKey === 'line1') {
                      return (
                        <p
                          key={index}
                          style={{
                            margin: isMobile
                              ? `0 0 ${mobileFont(8)} 0`
                              : '0 0 0.6vw 0',
                            fontSize: isMobile
                              ? mobileFont(12)
                              : '0.9vw',
                            fontWeight: 600,
                            lineHeight: '1.4',
                            textAlign: 'center',
                            width: '100%'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: text
                          }}
                        />
                      )
                    }

                    return (
                      <p
                        key={index}
                        style={{
                          margin: 0,
                          fontSize: isMobile
                            ? mobileFont(11)
                            : viewport >= 1920
                              ? '0.7vw'
                              : '0.8vw',
                          lineHeight: '1.35',
                          paddingLeft:
                            num === 1 && lineKey === 'line2'
                              ? '0.65em'
                              : 0,
                          marginTop:
                            num === 3 && lineKey === 'line5'
                              ? '0.4em'
                              : 0
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (
                            num === 4 ||
                            (num === 1 && lineKey === 'line2') ||
                            (num === 3 &&
                              (lineKey === 'line5' || lineKey === 'line6'))
                              ? text
                              : `• ${text}`
                          ).replace(/\n/g, '<br />')
                        }}
                      />
                    )
                  })
                )}

                {isFourthBox && (
                  <p
                    style={{
                      margin: isMobile
                        ? `${mobileFont(7)} 0 0 0`
                        : '0.5vw 0 0 0',
                      fontSize: isMobile
                        ? mobileFont(11)
                        : '0.8vw',
                      lineHeight: '1.35',
                      textAlign: 'center',
                      width: '100%'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: t('FOUNDING_REMAINING', {
                        count: foundingRemainingFormatted
                      })
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {showHint && supportsHover && (
        <div
          style={{
            position: 'fixed',
            top: cursorPos.y - 50,
            left: cursorPos.x,
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.85)',
            color: '#E7DFEC',
            padding: '6px 14px 8px 14px',
            borderRadius: 12,
            fontSize: 16,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5000,
            border: '1.5px solid #0A303B',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1
          }}
        >
          {t('INFOGRID_HOVER_HINT')}
        </div>
      )}

      {showHint2 && supportsHover && (
        <div
          style={{
            position: 'fixed',
            top: cursorPos.y - 50,
            left: cursorPos.x,
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.85)',
            color: '#E7DFEC',
            padding: '6px 14px 8px 14px',
            borderRadius: 12,
            fontSize: 16,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5000,
            border: '1.5px solid #0A303B',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1
          }}
        >
          {t('ADS_HOOVER')}
        </div>
      )}

      {showHint3 && supportsHover && (
        <div
          style={{
            position: 'fixed',
            top: cursorPos.y - 50,
            left: cursorPos.x,
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.85)',
            color: '#E7DFEC',
            padding: '6px 14px 8px 14px',
            borderRadius: 12,
            fontSize: 16,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5000,
            border: '1.5px solid #0A303B',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1
          }}
        >
          {t('INFOGRID_HOVER_HINT')}
        </div>
      )}

      <AppFeaturesModal
        open={showFeatureModal}
        anchorRect={anchorRect}
        onClose={() => setAnchorRect(null)}
      />

      <BenefitsModal
        open={showRegModal}
        anchorRect={anchorRect3}
        onClose={() => setAnchorRect3(null)}
      />

	<RegistrationModal
 	 open={showRegisterModal}
 	 onClose={() => {
  	  setShowRegisterModal(false)
 	   setHoverBox4(false)
 	 }}
	/>
    </>
  )
}

export default InfoGridModal
